import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { GET_CARRIER_DRIVER_TARIFFS, GET_CARRIER_ORDER_TARIFFS_FOR_SHIPPER, GET_SUBREGION_NAMES } from '../../queries';
import { ALL_RATES } from './constants';
import {
    describeRates,
    filterRates,
    getRateCategories,
    getSubregionIds,
    toDriverTariffs,
    toOrderTariffs,
} from './utils';

const withRateRows = (tariffs, serviceLevels, subregionNames) =>
    tariffs.map((tariff) => ({
        ...tariff,
        rates: describeRates(tariff.rates, { algoType: tariff.algoType, serviceLevels, subregionNames }),
    }));

/**
 * The signed-in carrier's order tariffs for this shipper and its driver tariffs, with `rates` as
 * display rows from `describeRates`.
 * @param {string} shipperId the shipper's client id
 * @returns {{ orderTariffs: Object[], driverTariffs: Object[], loading: boolean, error?: Error }}
 */
export const useCarrierTariffsForShipper = (shipperId) => {
    const { user_id, teammates, service_levels } = useClientUser();
    const orderRead = useQuery(GET_CARRIER_ORDER_TARIFFS_FOR_SHIPPER, {
        variables: { carrier_id: user_id, shipper_id: shipperId },
        skip: !user_id || !shipperId,
        onError: (e) => captureException(e),
    });
    const driverRead = useQuery(GET_CARRIER_DRIVER_TARIFFS, {
        variables: { carrier_id: user_id },
        skip: !user_id,
        onError: (e) => captureException(e),
    });

    const rawOrderTariffs = useMemo(() => toOrderTariffs(orderRead.data?.orderOverrides), [orderRead.data]);
    const rawDriverTariffs = useMemo(
        () => toDriverTariffs(driverRead.data?.driverMappings, teammates),
        [driverRead.data, teammates]
    );

    const subregionIds = useMemo(
        () => getSubregionIds([...rawOrderTariffs, ...rawDriverTariffs]),
        [rawOrderTariffs, rawDriverTariffs]
    );
    const subregionRead = useQuery(GET_SUBREGION_NAMES, {
        variables: { subregion_ids: subregionIds },
        skip: subregionIds.length === 0,
        onError: (e) => captureException(e),
    });
    const subregionNames = useMemo(
        () => Object.fromEntries((subregionRead.data?.subregions || []).map((s) => [s.subregion_id, s.name])),
        [subregionRead.data]
    );

    const orderTariffs = useMemo(
        () => withRateRows(rawOrderTariffs, service_levels, subregionNames),
        [rawOrderTariffs, service_levels, subregionNames]
    );
    const driverTariffs = useMemo(
        () => withRateRows(rawDriverTariffs, service_levels, subregionNames),
        [rawDriverTariffs, service_levels, subregionNames]
    );

    return {
        orderTariffs,
        driverTariffs,
        loading: orderRead.loading || driverRead.loading || subregionRead.loading,
        error: orderRead.error || driverRead.error || subregionRead.error,
    };
};

/**
 * The rate table's category filter and search, and the rows they leave.
 * @param {Object[]} rows a tariff's rates from `describeRates`
 * @returns {{ categories: { key: string, label: string }[], category: string, setCategory: Function, search: string, setSearch: Function, visibleRows: Object[] }}
 */
export const useRateFilters = (rows) => {
    const [category, setCategory] = useState(ALL_RATES.key);
    const [search, setSearch] = useState('');
    const categories = useMemo(() => getRateCategories(rows), [rows]);
    const visibleRows = useMemo(() => filterRates(rows, { category, search }), [rows, category, search]);
    return { categories, category, setCategory, search, setSearch, visibleRows };
};
